import { useState, useEffect, useMemo } from 'react';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import type { MatchInfo } from '../screens/Discover';
import { Portrait } from '../components/Portrait';
import { Img } from '../components/Img';
import { SoftButton } from '../components/common';
import { Icon } from '../components/icons';
import { useAuth } from '../hooks/useAuth';
import { hapticSuccess } from '../lib/haptics';

interface MatchSheetProps {
  match: MatchInfo;
  t: Theme;
  onClose: () => void;
  onOpenDate: () => void;
}

export function MatchSheet({ match: m, t, onClose, onOpenDate }: MatchSheetProps) {
  const { userProfile } = useAuth();
  const [show, setShow] = useState(false);
  const [confettiOn, setConfettiOn] = useState(true);
  useEffect(() => {
    const id = setTimeout(() => setShow(true), 40);
    hapticSuccess();
    const cid = setTimeout(() => setConfettiOn(false), 2500);
    return () => { clearTimeout(id); clearTimeout(cid); };
  }, []);

  // Deterministic confetti (no Math.random — keeps renders stable).
  const confetti = useMemo(() => {
    const colors = [t.terracotta, t.gold, t.sage, t.blush];
    return Array.from({ length: 14 }, (_, i) => ({
      left: (i * 41) % 100,
      size: 5 + (i % 4) * 2,
      delay: 200 + (i * 53) % 500,
      duration: 1300 + (i * 97) % 700,
      rotate: 360 + (i * 67) % 400,
      color: colors[i % 4],
      round: i % 3 === 0,
    }));
  }, [t]);

  const theirPhoto = m.photos?.[0] || null;
  const myPhoto = userProfile?.profile?.photos?.[0] || null;
  const mySeed = userProfile?.id?.slice(0, 4) || 'me';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute', inset: 0, zIndex: 200,
        background: 'rgba(31,26,23,0.55)', backdropFilter: 'blur(8px)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        opacity: show ? 1 : 0, transition: 'opacity 200ms',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: t.paper, borderRadius: '28px 28px 0 0',
          padding: '12px 0 28px', overflow: 'hidden', position: 'relative',
          transform: show ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 360ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        {/* Confetti — falls in front of the celebration, non-interactive */}
        {confettiOn && (
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 5 }}>
            {confetti.map((c, i) => (
              <span key={i} style={{
                position: 'absolute', top: -12, left: `${c.left}%`,
                width: c.size, height: c.round ? c.size : c.size * 1.6,
                borderRadius: c.round ? '50%' : 1, background: c.color,
                opacity: 0,
                ['--cr' as any]: `${c.rotate}deg`,
                animation: `pmConfetti ${c.duration}ms ease-in ${c.delay}ms forwards`,
              }} />
            ))}
          </div>
        )}

        {/* Handle */}
        <div style={{ width: 40, height: 4, borderRadius: 2, background: t.line, margin: '0 auto 12px' }} />

        {/* Photos overlap */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '8px 24px 6px', position: 'relative' }}>
          {myPhoto
            ? <Img className="pm-drift-l pm-d2" placeholder={t.paperAlt} src={myPhoto} alt="" style={{ width: 118, height: 148, borderRadius: 16, objectFit: 'cover', transform: 'rotate(-4deg) translateX(18px)', zIndex: 1, boxShadow: t.shadow }} />
            : <Portrait className="pm-drift-l pm-d2" seed={mySeed} label="You" style={{ width: 118, height: 148, borderRadius: 16, transform: 'rotate(-4deg) translateX(18px)', zIndex: 1, boxShadow: t.shadow }} />
          }
          {theirPhoto
            ? <Img className="pm-drift-r pm-d2" placeholder={t.paperAlt} src={theirPhoto} alt="" style={{ width: 118, height: 148, borderRadius: 16, objectFit: 'cover', transform: 'rotate(4deg) translateX(-18px)', zIndex: 2, boxShadow: t.shadow }} />
            : <Portrait className="pm-drift-r pm-d2" seed={m.matchId.slice(0, 4)} label={m.displayName} style={{ width: 118, height: 148, borderRadius: 16, transform: 'rotate(4deg) translateX(-18px)', zIndex: 2, boxShadow: t.shadow }} />
          }
          {/* Ring pulse behind the heart */}
          <span style={{
            position: 'absolute', width: 48, height: 48, borderRadius: 100,
            border: `2px solid ${t.terracotta}`, zIndex: 3, pointerEvents: 'none',
            animation: 'pmRingExpand 700ms ease-out 520ms backwards',
          }} />
          <div style={{
            position: 'absolute', width: 48, height: 48, borderRadius: 100,
            background: t.terracotta, color: '#FBF6EC',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4,
            boxShadow: t.shadow,
            animation: 'pmHeartBurst 600ms cubic-bezier(0.34,1.56,0.64,1) 420ms backwards',
          }}>{Icon.heart({ s: 22, c: '#fff', fill: '#fff', w: 0 })}</div>
        </div>

        {/* Text */}
        <div className="pm-fade-up pm-d4" style={{ textAlign: 'center', padding: '16px 32px 4px' }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 10.5, letterSpacing: 2, color: t.terracotta, textTransform: 'uppercase' }}>
            It's a preminade
          </div>
          <h2 style={{
            fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400,
            fontSize: 32, margin: '6px 0 4px', color: t.ink, letterSpacing: -0.4,
          }}>
            You and {m.displayName} fancy<br />each other.
          </h2>
          <p style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, margin: 0 }}>
            Concierge's drafting a date. Have a look before messaging?
          </p>
        </div>

        {/* Concierge card */}
        <div className="pm-fade-up pm-d5" style={{ padding: '18px 20px 6px' }}>
          <button onClick={onOpenDate} style={{ all: 'unset', cursor: 'pointer', width: '100%', boxSizing: 'border-box', display: 'block' }}>
            <div style={{
              padding: '16px 18px', borderRadius: 18,
              background: t.ink, color: t.paper,
              boxShadow: t.shadow, position: 'relative', overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: 100,
                background: `radial-gradient(circle, ${t.blush}, ${t.terracotta} 60%, transparent 75%)`,
                opacity: 0.6,
              }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, position: 'relative' }}>
                {Icon.sparkle({ s: 14, c: t.blush })}
                <span style={{ fontFamily: FONTS.mono, fontSize: 10.5, letterSpacing: 1.5, textTransform: 'uppercase', color: t.blush }}>
                  Concierge suggests
                </span>
              </div>
              <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, lineHeight: 1.15, position: 'relative' }}>
                "A night thoughtfully planned."
              </div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.paper, opacity: 0.7, marginTop: 6, position: 'relative' }}>
                Based on your combined taste profile · London
              </div>
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                <span style={{ fontFamily: FONTS.sans, fontSize: 12.5, color: t.blush, fontWeight: 600 }}>See the plan →</span>
              </div>
            </div>
          </button>
        </div>

        {/* Actions */}
        <div className="pm-fade-up pm-d6" style={{ padding: '12px 20px 4px', display: 'flex', gap: 10 }}>
          <SoftButton t={t} variant="outline" onClick={onClose} style={{ flex: 1 }}>Say hi first</SoftButton>
          <SoftButton t={t} variant="terracotta" onClick={onOpenDate} style={{ flex: 1 }}>Plan a date</SoftButton>
        </div>
      </div>
    </div>
  );
}
